import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import VideoIcon from '../VideoIcon';
import BasicLoader from '../BasicLoader';

import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import useMediaQuery from '../../hooks/useMediaQuery';

import s from './InsightsFullWidthVideo.module.scss';

const VIDEO_FIBER = 'fiber-loop';
const VIDEO_BESOKSNARING_LIGHT = 'besoksnaring-light-loop';
const VIDEO_BEFOLKNING = 'befolkning-loop';
const VIDEO_FORETAGANDE = 'foretagande-loop';

export const AVAILABLE_VIDEOS = [
  VIDEO_FIBER,
  VIDEO_BESOKSNARING_LIGHT,
  VIDEO_BEFOLKNING,
  VIDEO_FORETAGANDE,
];

// always set to false before commit!!
const DEBUG_MQS = false;

const videos = AVAILABLE_VIDEOS.reduce((videos, videoSlug) => {
  videos[videoSlug] = {
    small: [
      {
        src: `/video/${videoSlug}--800p--hevc.mp4`,
        type: 'video/mp4; codecs="hvc1"',
      },
      {
        src: `/video/${videoSlug}--800p--vp9.webm`,
        type: 'video/webm',
      },
    ],
    medium: [
      {
        src: `/video/${videoSlug}--1200p--hevc.mp4`,
        type: 'video/mp4; codecs="hvc1"',
      },
      {
        src: `/video/${videoSlug}--1200p--vp9.webm`,
        type: 'video/webm',
      },
    ],
    large: [
      {
        src: `/video/${videoSlug}--1800p--hevc.mp4`,
        type: 'video/mp4; codecs="hvc1"',
      },
      {
        src: `/video/${videoSlug}--1800p--vp9.webm`,
        type: 'video/webm',
      },
    ],
  };

  return videos;
}, {});

const InsightsFullWidthVideo = (props) => {
  const { videoSlug, layout, className, loaderColor, mediumQuery, largeQuery } =
    props;

  const mediumVideoMq = useMediaQuery(mediumQuery);
  const largeVideoMq = useMediaQuery(largeQuery);

  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoSrcs, setVideoSrcs] = useState(null);
  const [currentVideoSize, setCurrentVideoSize] = useState(null);

  useEffect(() => {
    const videoS = videoSlug in videos ? videos[videoSlug] : null;

    if (videoS && !mediumVideoMq && !largeVideoMq) {
      if (DEBUG_MQS) {
        setCurrentVideoSize('small');
      }

      setVideoSrcs(videoS.small);
    } else if (videoS && mediumQuery && mediumVideoMq && !largeVideoMq) {
      if (DEBUG_MQS) {
        setCurrentVideoSize('medium');
      }

      setVideoSrcs(videoS.medium);
    } else if (videoS && largeQuery && largeVideoMq) {
      if (DEBUG_MQS) {
        setCurrentVideoSize('large');
      }

      setVideoSrcs(videoS.large);
    }
  }, [mediumVideoMq, largeVideoMq]);

  const [inViewRef, isVisible] = useIntersectionObserver(
    [0, 0.25, 0.5, 0.75, 1],
    `-100px 0px 0px 0px`,
    true
  );

  const rootCls = classNames(s.Root, className, 'grid', {
    [s.Visible]: isVisible,
    [s.VideoLoaded]: videoLoaded,
    [s.LayoutExtraWide]: layout === 'extra-wide',
  });

  return (
    <div className={rootCls}>
      {DEBUG_MQS && <h1>DEBUG_MQS: {currentVideoSize}</h1>}
      <div ref={inViewRef} className={s.Container}>
        {isVisible && videoSrcs && (
          <VideoIcon
            className={s.Video}
            active={true}
            loop={true}
            srcs={videoSrcs}
            onVideoLoaded={() => {
              setVideoLoaded(true);
            }}
          />
        )}
        <div className={s.LoaderWrap}>
          <div className={s.Loader}>
            <BasicLoader theme={loaderColor} />
          </div>
        </div>
      </div>
    </div>
  );
};

InsightsFullWidthVideo.propTypes = {
  className: PropTypes.string,
  mediumQuery: PropTypes.string,
  largeQuery: PropTypes.string,
  layout: PropTypes.oneOf(['normal', 'extra-wide']),
  videoSlug: PropTypes.oneOf(AVAILABLE_VIDEOS),
  loaderColor: PropTypes.oneOf(['light', 'dark']),
};

InsightsFullWidthVideo.defaultProps = {
  videoSlug: null,
  layout: 'normal',
  loaderColor: 'light',
  mediumQuery: '(min-width: 800px)',
};

export default InsightsFullWidthVideo;
